import { useCallback, useState, useEffect, useMemo } from 'react';
import './App.css';
import Config from './Config';

import Sidebar from './Components/SideBar/SideBar';
import CalendarView from './Components/CalendarView/CalendarView';
import CalendarSettings from './Components/CalendarSettings/CalendarSettings';
import CalendarInfoBox from './Components/CalendarInfoBox/CalendarInfoBox';
import CalenderExportDialog from './Components/CalenderExportDialog/CalenderExportDialog';

function App() {
  // --- UI State ---
  const [isMenuOpen, setIsMenuOpen] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isInfoBoxOpen, setIsInfoBoxOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  // --- User State ---
  const [userRights, setUserRights] = useState(null);
  const [currentUser, setCurrentUser] = useState('');


  // --- Daten State ---
  const [calendars, setCalendars] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState(null);

  // ----------------------------
  // Helpers
  // ----------------------------
  const log = (...args) => console.log('[Kalendarium]', ...args);
  const warn = (...args) => console.warn('[Kalendarium]', ...args);
  const err = (...args) => console.error('[Kalendarium]', ...args);

  // ----------------------------
  // Laden beim Start
  // ----------------------------
  useEffect(() => {
    loadCalendars();
    loadUserRights();
  }, []);

  useEffect(() => {
    loadCalendars();
  }, [userRights]);

  const loadUserRights = () => {
    log('Loading user info… (GET /getUser.php)');
    fetch('/getUser.php')
      .then(res => {
        log('Response /getUser.php:', res.status, res.statusText);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(data => {
        log('Loaded user data:', data);
        setUserRights(data.rights || null);
        const name = data?.username ?? '';
        setCurrentUser(name);
        console.log('[Kalendarium] Userdata set to:', { rights: data.rights, username: name });
      })
      .catch(e => err('loadUser failed:', e));
  };

  const loadCalendars = (selectCalendarID) => {
    console.log('[Kalendarium] loadCalendars called, selectCalendarID=', selectCalendarID);

    log('Loading calendars… (GET /load_calendars.php)');
    fetch('/load_calendars.php')
      .then(res => {
        log('Response /load_calendars.php:', res.status, res.statusText);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(data => {
        log('Loaded calendars data:', data);

        // Safety: Struktur normalisieren
        const normalized = Array.isArray(data)
          ? data.map(c => ({
            id: c.id ?? Date.now().toString(16),
            name: c.name ?? 'Unbenannt',
            events: Array.isArray(c.events) ? c.events : []
          }))
          : [];

        log('Loaded calendars normalized:', normalized);
        setCalendars(normalized);

        // Falls nichts ausgewählt ist, ersten Kalender wählen
        if (!selectedCalendar && normalized.length > 0) {
          selectCalendar(normalized[0]);
          log('Auto-selected first calendar:', normalized[0]?.name);
        }
      })
      .catch(e => err('load_calendars failed:', e));
  };

  // ----------------------------
  // Speichern eines Kalenders
  // ----------------------------
  const saveCalendar = (calendar) => {
    const cleaned = {
      ...calendar,
      events: (calendar.events || [])
        .filter(e => e && e.start && e.end)
        .map(e => ({
          ...e,
          title: e.title ?? '',
          description: e.description ?? '',
          location: e.location ?? '',
          recurrence: e.recurrence ?? 'none',
          start: typeof e.start === 'string' ? e.start : new Date(e.start).toISOString(),
          end: typeof e.end === 'string' ? e.end : new Date(e.end).toISOString(),
        }))
    };

    log('Saving calendar (POST /save_calendar.php) — cleaned payload:', cleaned);

    return fetch('/save_calendar.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cleaned)
    })
      .then(r => {
        log('Response /save_calendar.php:', r.status, r.statusText);
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(j => {
        log('save_calendar.php JSON:', j);
        if (!j.ok) throw new Error(j.error || 'Save failed');
        log('Saved OK:', j.file);
      })
      .catch(e => err('save_calendar failed:', e));
  };

  // ----------------------------
  // Aktionen
  // ----------------------------
  useEffect(() => {
    if (selectedCalendar) {
      const fresh = calendars.find(c => c.id === selectedCalendar.id);
      if (fresh && fresh !== selectedCalendar) setSelectedCalendar(fresh);
    } else if (calendars.length > 0) {
      setSelectedCalendar(calendars[0]);
    }
  }, [calendars]); // <- wichtig

  const toggleMenu = () => {
    setIsMenuOpen(v => !v);
    log('Sidebar toggled →', !isMenuOpen ? 'open' : 'closed');
  };

  const handleLogOut = () => {
    fetch(Config.LoginURL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ action: "logout" }),
    })
      .then(res => {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(data => {
        log('Logout response:', data);
        if (data.success) {
          window.location.reload();
        }
      })
      .catch(e => err('logout failed:', e));
  };

  const addCalendar = () => {
    const name = prompt('Name des neuen Kalenders:');
    const id = Date.now().toString(16);

    if (!name) {
      console.warn('[Kalendarium] addCalendar: abgebrochen/kein Name');
      return;
    }

    const newCalendar = { id: id, name: name, events: [] };
    console.log('[Kalendarium] Adding calendar:', newCalendar);

    setCalendars(prev => {
      const next = [...prev, newCalendar];
      console.log('[Kalendarium] Calendars after add:', next);
      return next;
    });
    setSelectedCalendar(newCalendar);
    console.log('[Kalendarium] Selected calendar →', newCalendar.id);

    fetch('/save_calendar.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, name, events: [] })
    })
      .then(r => {
        console.log('[Kalendarium] Response /save_calendar.php (create):', r.status, r.statusText);
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(j => {
        console.log('[Kalendarium] Created ICS:', j);
        if (!j.ok) throw new Error(j.error || 'Save failed');
        createCalendarConfig(id, name);
      })
      .catch(e => console.error('[Kalendarium] create calendar failed:', e));
  };

  const createCalendarConfig = (id, name) => {
    const BUILTIN_COLUMNS = [
      { kind: 'builtin', key: 'DATE', title: 'Datum', export: true },
      { kind: 'builtin', key: 'SUMMARY', title: 'Titel', merge: false, export: true },
      { kind: 'builtin', key: 'DESCRIPTION', title: 'Beschreibung', merge: false, export: true },
      { kind: 'builtin', key: 'LOCATION', title: 'Ort', merge: false, export: true },
    ];

    const EXPORTSETTINGS = {
      Name: name,
      ID: id,
      Logo: "default.png",
      HeaderSize: 12,
      HeaderBackgroundColor: '#ffffff',
      HeaderFontColor: '#000000',
      HeaderColor: '#ffffffff',
      FooterText: ''
    };

    const content = JSON.stringify({ exportSettings: EXPORTSETTINGS, config: BUILTIN_COLUMNS });
    console.log("create config file content:", content);

    fetch('/save_calendar_config.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: content,
    })
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(j => {
        if (!j.ok) throw new Error(j.error || 'Save failed');
      })
      .catch(e => console.log(e.message));
  };

  const selectCalendar = (calendar) => {
    setSelectedCalendar(calendar);
    log('Selected calendar:', calendar?.id, calendar);
  };

  const handleDeleteCalendar = (id) => {
    log('Deleting calendar id:', id);
    setCalendars(prev => {
      const next = prev.filter(cal => cal.id !== id);
      log('Calendars after delete:', next);
      return next;
    });
    if (selectedCalendar?.id === id) {
      setSelectedCalendar(null);
      log('Deleted selected calendar → selection cleared');
    }

    fetch('/delete_calendar.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: id, name: selectedCalendar.name })
    })
      .then(r => r.json())
      .then(console.log)
      .catch(console.error);
  };

  const handleExportCalendar = (id) => {
    const cal = calendars.find(c => c.id === id);
    if (!cal) return;

    selectCalendar(cal);
    setIsExportDialogOpen(true);

  };

  const onExportCalender = ({ calendar, startDate, endDate }) => {
    fetch('/export_calendar.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: calendar.id, name: calendar.name, startDate: startDate, endDate: endDate })
    })
      .then(r => r.json())
      .then(j => {
        if (!j.ok) throw new Error(j.error || 'Export failed');
        window.open('/' + j.file, '_blank');
      })
      .catch(console.error);

  };

  const handleDisplayLink = () => {
    setIsInfoBoxOpen(true);
  };

  const handleAddEvent = (calendarId, event) => {
    log('Add event to calendar:', calendarId, 'event:', event);

    setCalendars(prev => {
      const next = prev.map(cal =>
        cal.id === calendarId
          ? { ...cal, events: [...(cal.events || []), event] }
          : cal
      );

      const updated = next.find(c => c.id === calendarId);
      log('Calendar after add (in-memory):', updated);

      if (selectedCalendar?.id === calendarId) {
        setSelectedCalendar(updated);
        log('Selected calendar updated (after add).');
      }

      saveCalendar(updated);

      return next;
    });
  };

  const handleUpdateEvent = (calendarId, updatedEvent) => {
    console.log('Update event in calendar:', calendarId, 'event:', updatedEvent);

    setCalendars(prev => {
      const next = prev.map(cal => {
        if (cal.id !== calendarId) return cal;

        const events = [...(cal.events || [])];
        const eventIndex = events.findIndex(ev => ev.id === updatedEvent.id);

        console.log('Found event index:', eventIndex, 'in events:', events);

        if (eventIndex !== -1) {
          events[eventIndex] = updatedEvent;
          console.log('Updated event');
        } else {
          console.warn('Event to update not found');
        }

        if (!updatedEvent.start && !updatedEvent.end) {
          events.splice(eventIndex, 1);
          console.log('Removed event with null start and end');
        }

        return { ...cal, events };
      });
      const updatedCal = next.find(c => c.id === calendarId);

      if (selectedCalendar?.id === calendarId) setSelectedCalendar(updatedCal);

      saveCalendar(updatedCal);

      console.log('[Kalendarium] Calendar after update (in-memory):', updatedCal);
      return next;
    });
  };

  const handleShowSettings = (calendarId) => {
    const cal = calendars.find(c => c.id === calendarId);
    if (!cal) return;

    setSelectedCalendar(cal);
    setIsSettingsOpen(true);
  };

  // einmalige Helper
  function getCan(action, rights, calendarId, username) {
    if (!rights || !calendarId) return false;
    const r1 = rights?.[calendarId]?.[action];
    const r2 = username ? rights?.[calendarId]?.[username]?.[action] : undefined;
    const r3 = username ? rights?.[username]?.[calendarId]?.[action] : undefined;
    const r4 = rights?.[action];
    return !!(r1 ?? r2 ?? r3 ?? r4);
  }

  // universelle Prüffunktion (optional calendarId; default: ausgewählter)
  const can = useCallback(
    (action, id = selectedCalendar?.id) => getCan(action, userRights, id, currentUser),
    [userRights, currentUser, selectedCalendar?.id]
  );

  // Funktionen für beliebige IDs:
  const canReadFor = useCallback((id) => can('read', id), [can]);
  const canWriteFor = useCallback((id) => can('write', id), [can]);
  const canDeleteFor = useCallback((id) => can('delete', id), [can]);



  // ----------------------------
  // Render
  // ----------------------------
  return (
    <div className="app">
      <Sidebar
        calendars={calendars}
        selectedCalendar={selectedCalendar}
        onSelect={selectCalendar}
        onAdd={addCalendar}
        onShowLink={handleDisplayLink}
        onDelete={handleDeleteCalendar}
        onExport={handleExportCalendar}
        onShowSettings={handleShowSettings}
        isOpen={isMenuOpen}
        toggleMenu={toggleMenu}
        onLogout={handleLogOut}

        canDelete={canDeleteFor}
        canRead={canReadFor}
        canWrite={canWriteFor}
      />

      {isSettingsOpen && (
        <CalendarSettings
          calendarid={selectedCalendar.id}
          currentUsername={currentUser}
          onClose={(calendarid, calendarName) => {
            setIsSettingsOpen(false);
            if (!calendarid) return;
            const newCalendar = { ...selectedCalendar, id: calendarid, name: calendarName };
            setSelectedCalendar(newCalendar);
            saveCalendar(newCalendar);
            loadCalendars(calendarid);
          }}
          onSaved={() => {
            loadUserRights();
          }}
        />
      )}

      {isInfoBoxOpen && (
        <CalendarInfoBox
          calendar={selectedCalendar}
          onClose={() => setIsInfoBoxOpen(false)}
        />
      )}


      {isExportDialogOpen && (
        <CalenderExportDialog
          calendar={selectedCalendar}
          onClose={() => setIsExportDialogOpen(false)}
          onExport={onExportCalender}
        />
      )}

      <div className="main">
        {selectedCalendar ? (
          <CalendarView
            key={selectedCalendar.id}
            calendar={selectedCalendar}
            canDelete={canDeleteFor}
            canWrite={canWriteFor}
            canRead={canReadFor}
            onDeleteCalendar={(id) => {
              log('Delete from CalendarView request:', id);
              handleDeleteCalendar(id);
            }}
            onUpdateEvent={(calendarId, event) => handleUpdateEvent(calendarId, event)}
            onAddEvent={(event) => {
              log('onAddEvent from CalendarView:', event);
              if (!selectedCalendar?.id) {
                return warn('Cannot add event: no selected calendar');
              }
              handleAddEvent(selectedCalendar.id, event);
            }}
          />
        ) : (
          <>
            <h1>Willkommen zu Kalendarium</h1>
            <p>Wähle einen Kalender aus der Liste links aus, um zu starten.</p>
          </>
        )}
      </div>
    </div>
  );
}

export default App;
